import React, { useEffect, useState } from 'react';
import {
  disableTwoFactor,
  enableTwoFactor,
  getTwoFactorStatus,
  setupTwoFactor,
} from '../../services/api';

// Шаги мастера. Пароль спрашивается заново даже у вошедшего пользователя:
// иначе оставленная без присмотра сессия позволяет постороннему привязать
// свой телефон, и второй фактор начинает работать против владельца.
const STEP = {
  IDLE: 'idle',
  PASSWORD: 'password',
  SCAN: 'scan',
  CODES: 'codes',
  DISABLE: 'disable',
};

const TwoFactorCard = () => {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(STEP.IDLE);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => getTwoFactorStatus().then(setStatus).catch(() => setStatus(null));
  useEffect(() => { load(); }, []);

  const reset = () => {
    setStep(STEP.IDLE);
    setPassword('');
    setCode('');
    setSetupData(null);
    setError('');
  };

  const fail = (e, fallback) =>
    setError(e?.response?.data?.error || fallback);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      setSetupData(await setupTwoFactor(password));
      setStep(STEP.SCAN);
    } catch (err) {
      fail(err, 'Не удалось начать настройку');
    } finally {
      setBusy(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await enableTwoFactor(code);
      setBackupCodes(data.backup_codes);
      setStep(STEP.CODES);
      setCode('');
      setPassword('');
      load();
    } catch (err) {
      fail(err, 'Код не подошёл');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await disableTwoFactor(password, code);
      reset();
      load();
    } catch (err) {
      fail(err, 'Не удалось отключить');
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <section className="ui-card">
      <div className="ui-card-head">
        <h2>Двухфакторная аутентификация</h2>
        <span className={status.enabled ? 'ui-badge ok' : 'ui-badge'}>
          {status.enabled ? 'включена' : 'выключена'}
        </span>
      </div>

      <p className="ui-dim">
        Второй фактор — код из приложения-аутентификатора (Google Authenticator,
        Яндекс Ключ, 1Password и любое другое). Без него пароль остаётся
        единственным, что отделяет постороннего от зарплат, табелей и переписки.
      </p>

      {status.enabled && step === STEP.IDLE && (
        <>
          <p className="ui-dim">
            Резервных кодов осталось: <strong>{status.backup_codes_left}</strong>
            {status.backup_codes_left <= 2 && ' — их стоит перевыпустить, отключив и включив 2FA заново.'}
          </p>
          <button type="button" className="ui-btn-secondary" onClick={() => setStep(STEP.DISABLE)}>
            Отключить
          </button>
        </>
      )}

      {!status.enabled && step === STEP.IDLE && (
        <button type="button" className="ui-btn-primary" onClick={() => setStep(STEP.PASSWORD)}>
          Включить
        </button>
      )}

      {step === STEP.PASSWORD && (
        <form onSubmit={handleSetup} className="ui-form">
          <label htmlFor="tfa-pass">Подтвердите пароль</label>
          <input
            id="tfa-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
          <div className="ui-form-actions">
            <button type="submit" className="ui-btn-primary" disabled={busy}>
              {busy ? 'Проверяем…' : 'Продолжить'}
            </button>
            <button type="button" className="ui-btn-secondary" onClick={reset}>Отмена</button>
          </div>
        </form>
      )}

      {step === STEP.SCAN && setupData && (
        <form onSubmit={handleEnable} className="ui-form">
          <p>Отсканируйте код в приложении-аутентификаторе:</p>
          <img src={setupData.qr} alt="QR-код для настройки" className="ui-qr" width="180" height="180" />
          <p className="ui-dim">
            Если камера недоступна, введите ключ вручную:
            <br />
            <code>{setupData.secret}</code>
          </p>
          <label htmlFor="tfa-code">Введите код из приложения</label>
          <input
            id="tfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="6 цифр"
            autoFocus
            required
          />
          <div className="ui-form-actions">
            <button type="submit" className="ui-btn-primary" disabled={busy}>
              {busy ? 'Проверяем…' : 'Включить'}
            </button>
            <button type="button" className="ui-btn-secondary" onClick={reset}>Отмена</button>
          </div>
        </form>
      )}

      {step === STEP.CODES && backupCodes && (
        <div className="ui-form">
          <p>
            <strong>Сохраните резервные коды.</strong> Каждый работает один раз и
            нужен, если телефон потеряется. Показываются они только сейчас — в
            базе хранятся хеши, восстановить их не сможем даже мы.
          </p>
          <ul className="ui-backup-codes">
            {backupCodes.map((c) => <li key={c}><code>{c}</code></li>)}
          </ul>
          <button
            type="button"
            className="ui-btn-primary"
            onClick={() => { setBackupCodes(null); reset(); }}
          >
            Я сохранил коды
          </button>
        </div>
      )}

      {step === STEP.DISABLE && (
        <form onSubmit={handleDisable} className="ui-form">
          <label htmlFor="tfa-dis-pass">Пароль</label>
          <input
            id="tfa-dis-pass"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
          <label htmlFor="tfa-dis-code">Код из приложения или резервный код</label>
          <input
            id="tfa-dis-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          <div className="ui-form-actions">
            <button type="submit" className="ui-btn-danger" disabled={busy}>
              {busy ? 'Отключаем…' : 'Отключить'}
            </button>
            <button type="button" className="ui-btn-secondary" onClick={reset}>Отмена</button>
          </div>
        </form>
      )}

      {error && <div className="error">{error}</div>}
    </section>
  );
};

export default TwoFactorCard;
